import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { HttpErrorResponse } from '@angular/common/http';

import { ModuleConfiguration } from '../security.module.config';

import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as securityActions from '../../security/store/actions';
import * as userActions from '../store/actions';
import { RolesService } from './RolesService';
import { RoleListModel } from '../models/RoleListModel';
import { AddRoleModel } from '../models/AddRoleModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { RoleModel } from '../models/RoleModel';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ResponseMessage } from '../../core/services/ResponseMessage';
import { ResponseMessageType } from '../../core/services/ResponseMessageType';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { LoadPageAction } from '../../pillar/common/actions/LoadPageAction';
import { LookupModel } from '../../common/models/LookupModel';
import { ActionError } from '../../pillar/common/actions/ActionError';
import { SystemPermissionModel } from '../models/SystemPermissionModel';
import { DeleteRoleModel } from '../models/DeleteRoleModel';

@Injectable()
export class RolesServiceEffects {
  @Effect()
  getRoleList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<RoleListModel>(ModuleConfiguration.moduleName, RoleListModel))
    .map((a: dashboardActions.QueryListAction<RoleListModel>) => a.payload)
    .switchMap((payload: LoadPagePayload) => this.rolesService.getRoleList(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm)
      .map((response: QueryResponse<RoleListModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, RoleListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, RoleListModel, { items: response.items, totalCount: response.totalCount });
      }));
  @Effect()
  getAvailablePermissions$: Observable<Action> = this.actions$.ofType(securityActions.QUERY_AVAILABLE_PERMISSIONS)
    .switchMap((action: LoadPageAction) => this.rolesService.getAvailablePermissions(action.payload.page, action.payload.pageSize, action.payload.sort, action.payload.sortDirection, action.payload.searchTerm)
      .map((response: QueryResponse<any>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new securityActions.QueryAvailablePermissionsSuccessAction({ items: response.items });
      }));
  @Effect()
  getRolesByType$: Observable<Action> = this.actions$.ofType(securityActions.QUERY_AVAILABLE_ROLES_BY_TYPE)
      .switchMap((action: securityActions.QueryAvailableRolesByTypeAction) => this.rolesService.getAssignableRolesForType(action.roleType,action.payload.page, action.payload.pageSize, action.payload.sort, action.payload.sortDirection, action.payload.searchTerm)
      .map((response: QueryResponse<any>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new securityActions.QueryAvailableRolesByTypeSuccessAction(action.roleType, { items: response.items });
      }));
  @Effect()
  getRole$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType<RoleModel>(ModuleConfiguration.moduleName, RoleModel))
    .switchMap((a: dashboardActions.GetSelectionAction<RoleModel>) => this.rolesService.getRole(a.payload.key)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<RoleModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<RoleModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, RoleListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, RoleModel, { model: response.model });
      }));
  @Effect()
  addRole$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddRoleModel>(ModuleConfiguration.moduleName, AddRoleModel))
    .switchMap((action: dashboardActions.AddAction<AddRoleModel>) => this.rolesService.addRole(action.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, RoleListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddRoleModel, { key: response.model });
      }));
  @Effect()
  addPermission$: Observable<Action> = this.actions$.ofType(securityActions.ADD_PERMISSION_TO_ROLE)
    .switchMap((action: securityActions.AddPermissionToRoleAction) => this.rolesService.addPermission(action.payload.roleKey, action.payload.permission)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<LookupModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<SystemPermissionModel>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.filter(m => m.type !== ResponseMessageType.Error).map(m => m.value));
        }
        return new userActions.AddPermissionToRoleSuccessAction({ roleKey: action.payload.roleKey, permission: response.model });
      }));
  @Effect()
  removePermission$: Observable<Action> = this.actions$.ofType(securityActions.REMOVE_PERMISSION_TO_ROLE)
    .switchMap((action: securityActions.RemovePermissionToRoleAction) => this.rolesService.removePermission(action.payload.roleKey, action.payload.permission.key)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<boolean>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.filter(m => m.type !== ResponseMessageType.Error).map(m => m.value));
        }
        return new securityActions.RemovePermissionToRoleSuccessAction({ roleKey: action.payload.roleKey, permission: action.payload.permission });
      }));
  @Effect()
  addIncludedRole$: Observable<Action> = this.actions$.ofType(securityActions.ADD_ROLE_TO_ROLE)
    .switchMap((action: securityActions.AddRoleToRoleAction) => this.rolesService.addIncludedRole(action.payload.roleKey, action.payload.role)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<RoleModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<RoleModel>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.filter(m => m.type !== ResponseMessageType.Error).map(m => m.value));
        }
        return new userActions.AddRoleToRoleSuccessAction({ roleKey: action.payload.roleKey, role: response.model });
      }));
  @Effect()
  removeIncludedRole$: Observable<Action> = this.actions$.ofType(securityActions.REMOVE_ROLE_FROM_ROLE)
    .switchMap((action: securityActions.RemoveRoleFromRoleAction) => this.rolesService.removeIncludedRole(action.payload.roleKey, action.payload.role.key)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<boolean>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.filter(m => m.type !== ResponseMessageType.Error).map(m => m.value));
        }
        return new securityActions.RemoveRoleFromRoleSuccessAction({ roleKey: action.payload.roleKey, role: action.payload.role });
      }));
  @Effect()
  reviseName$: Observable<Action> = this.actions$
    .ofType(workspaceActions.updateModelType<RoleModel, string>(ModuleConfiguration.moduleName, RoleModel, 'name'))
    .switchMap((action: workspaceActions.UpdateModelAction<RoleModel, 'name'>) => this.rolesService.reviseName(action.payload.key, action.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<string>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: action,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(
          ModuleConfiguration.moduleName,
          RoleModel,
          'name',
          {
            key: action.payload.key,
            model: response.model,
            control: action.payload.control
          });
      }));
  @Effect()
  reviseIsAdministrative$: Observable<Action> = this.actions$
    .ofType(workspaceActions.updateModelType<RoleModel, boolean>(ModuleConfiguration.moduleName, RoleModel, 'isAdministrative'))
      .switchMap((action: workspaceActions.UpdateModelAction<RoleModel, boolean>) => this.rolesService.reviseIsAdministrative(action.payload.key, action.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<boolean>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
        .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: action,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(
          ModuleConfiguration.moduleName,
          RoleModel,
          'isAdministrative',
          {
            key: action.payload.key,
            model: response.model,
            control: action.payload.control
          });
      }));
  @Effect()
  reviseDescription$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<RoleModel, string>(ModuleConfiguration.moduleName, RoleModel, 'description'))
    .switchMap((action: workspaceActions.UpdateModelAction<RoleModel, string>) => this.rolesService.reviseDescription(action.payload.key, action.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<string>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: action,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, RoleModel, 'description', { key: action.payload.key, model: response.model, control: action.payload.control });
      }));
  @Effect()
  deleteRole$: Observable<Action> = this.actions$.ofType(dashboardActions.deleteType<DeleteRoleModel>(ModuleConfiguration.moduleName, DeleteRoleModel))
    .switchMap((action: dashboardActions.DeleteAction<DeleteRoleModel>) => this.rolesService.deleteRole(action.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new dashboardActions.DeleteSuccessAction(ModuleConfiguration.moduleName, DeleteRoleModel, { key: response.model });
      }));
      @Effect()
      reviseIsEssential$: Observable<Action> = this.actions$
        .ofType(workspaceActions.updateModelType<RoleModel, boolean>(ModuleConfiguration.moduleName, RoleModel, 'isEssential'))
          .switchMap((action: workspaceActions.UpdateModelAction<RoleModel, boolean>) => this.rolesService.reviseIsEssential(action.payload.key, action.payload.model)
          .catch((error: HttpErrorResponse) => {
            var response = new ModelResponse<boolean>();
            response.hasError = true;
            var responseMessage = new ResponseMessage();
            responseMessage.type = ResponseMessageType.Error;
            responseMessage.value = error.message;
            response.messages = [responseMessage];
            return Observable.of(response);
          })
            .map((response: ModelResponse<boolean>) => {
            if (response.hasError) {
              return new workspaceActions.UpdateModelErrorAction(
                {
                  action: action,
                  errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
                });
            }
            return new workspaceActions.UpdateModelSuccessAction(
              ModuleConfiguration.moduleName,
              RoleModel,
              'isEssential',
              {
                key: action.payload.key,
                model: response.model,
                control: action.payload.control
              });
          }));
  constructor(
    private rolesService: RolesService,
    private actions$: Actions
  ) { }
}



// WEBPACK FOOTER //
// ./src/app/security/services/RolesService.effects.ts